import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { EducationService } from './EducationService'
import {
  EducationDataInitialState,
  LessonRequestData,
  ProgramData,
  QuizData,
  QuizRequestData,
  RewardsData,
  SaveQuizRequestData,
  UserRewardRequestData,
} from './types'

const initialState: EducationDataInitialState = {
  programs: [] as ProgramData[],
  quizzes: [] as QuizData[],
  rewards: [] as RewardsData[],
}

export const fetchPrograms = createAsyncThunk('education/fetchPrograms', async () => {
  try {
    const { data: programsData } = await EducationService.getPrograms()
    return programsData
  } catch (error) {
    throw Error(error.message)
  }
})

export const completeLesson = createAsyncThunk(
  'education/completeLesson',
  async (requestData: LessonRequestData) => {
    try {
      await EducationService.changeLessonStatus(requestData)
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const fetchQuiz = createAsyncThunk(
  'education/fetchQuiz',
  async (requestData: QuizRequestData) => {
    try {
      const { data: quizData } = await EducationService.getQuiz(requestData)
      return quizData
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const sendQuiz = createAsyncThunk(
  'education/saveQuiz',
  async (requestData: SaveQuizRequestData[]) => {
    try {
      const { data: saveQuizData } = await EducationService.sendQuiz(requestData)
      return saveQuizData
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const fetchRewards = createAsyncThunk('education/fetchRewards', async () => {
  try {
    const { data: rewardsData } = await EducationService.getRewards()
    return rewardsData
  } catch (error) {
    throw Error(error.message)
  }
})

export const fetchUserReward = createAsyncThunk(
  'education/fetchUserReward',
  async (requestData: UserRewardRequestData) => {
    try {
      const { data: userRewardData } = await EducationService.getUserReward(requestData)
      return userRewardData
    } catch (error) {
      throw Error(error.message)
    }
  },
)

const EducationSlice = createSlice({
  name: 'Education',
  initialState,
  reducers: {
    updateLessonStatus: (state, action) => {
      const lessonId = action.payload
      state.programs = state.programs.map((program) => {
        return {
          ...program,
          modules: program.modules.map((module) => {
            return {
              ...module,
              lessons: module.lessons.map((lesson) => {
                if (lesson.lessonId === lessonId) return { ...lesson, completed: !lesson.completed }
                return lesson
              }),
            }
          }),
        }
      })
    },
    saveAnswer: (state, action) => {
      const { programId, questionId, answerId } = action.payload
      const quiz = state.quizzes.find((quiz) => quiz.programId === programId)
      const question = quiz?.questions.find((question) => question.questionId === questionId)
      if (question) question.userAnswer = answerId
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrograms.fulfilled, (state, action) => {
        state.programs = action.payload
      })
      .addCase(fetchQuiz.fulfilled, (state, action) => {
        const responseData = action.payload
        const isQuiz = !!state.quizzes.find((quiz) => quiz.programId === responseData.programId)
        if (!isQuiz) {
          state.quizzes.push(action.payload)
        }
      })
      .addCase(sendQuiz.fulfilled, (state, action) => {
        const responseData = action.payload
        const program = state.programs.find(
          (program) => program.programId === responseData.programId,
        )
        if (program) {
          program.lastQuizDate = responseData.lastQuizDate
        }
        const quiz = state.quizzes.find((quiz) => quiz.programId === responseData.programId)
        if (quiz) {
          quiz.totalAnswers = responseData.totalAnswers
          quiz.correctAnswers = responseData.correctAnswers
        }
        if (program && responseData.totalAnswers === responseData.correctAnswers) {
          program.isQuizCompleted = true
        }
      })
      .addCase(fetchRewards.fulfilled, (state, action) => {
        const responseData = action.payload

        /* eslint-disable indent */
        state.rewards = state.rewards.length
          ? state.rewards.map((reward) => {
              const newReward = responseData.find((item) => item.programId === reward.programId)
              if (newReward) {
                return { ...reward, ...newReward }
              }
              return reward
            })
          : responseData
        /* eslint-disable indent */
      })

      .addCase(fetchUserReward.fulfilled, (state, action) => {
        const responseData = action.payload

        state.rewards = state.rewards.map((reward) => {
          if (reward.programId === responseData.programId) {
            return { ...reward, userReward: responseData }
          }
          return reward
        })
      })
  },
})

const EducationReducer = EducationSlice.reducer
export const { updateLessonStatus } = EducationSlice.actions
export const { saveAnswer } = EducationSlice.actions

export default EducationReducer
